import React from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  TextField,
  Grid,
} from '@mui/material';
import { Field } from 'formik';
import CptTemplateTextField from './CptTemplateTextField';

const TemplateLanguageFieldsTable = ({
  rows,
  title,
  showControls = true,
  showComments = false,
  valuesSection = 'template_language_fields',
  commentsFieldName = 'template_language_fields.comments',
  values,
  handleChange,
  handleBlur,
  isFormDisabled,
  templateLoading,
  templateData,
  templateSelections,
  templateCheckboxDisabled,
  templateLlmLoadingField = null,
  handleTemplateCheckboxChange,
  setValues,
  setFieldValue,
  onClearAll,
  cptColoredPreview = false,
  hideTemplateColumn = false,
}) => {
  const commentsValue = commentsFieldName.split('.').reduce((obj, key) => obj?.[key], values) ?? '';

  return (
  <Box sx={{ mb: showComments ? 0 : 3 }}>
    {title && (
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
        {title}
      </Typography>
    )}

    {showControls && (
      <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, border: '1px solid #ddd' }}>
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
          Template Language Options
          {templateLoading && <CircularProgress size={16} sx={{ ml: 2 }} />}
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Template Status:
            {templateLoading
              ? ' Loading...'
              : templateData
                ? ` Loaded (${Object.keys(templateData).length} fields available)`
                : ' No templates available'}
          </Typography>
          {!templateData && !templateLoading && (
            <Typography variant="caption" color="warning.main" sx={{ ml: 1 }}>
              - Checkboxes will be disabled until templates are loaded
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button
            variant="outlined"
            size="small"
            onClick={onClearAll}
            disabled={isFormDisabled || templateLoading}
            sx={{ minWidth: '110px' }}
          >
            Clear All Templates
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
            Check a box once to generate tailored template language with AI. Click an already-checked
            box again to regenerate. <strong>Shift+click</strong> a checked box to clear it.
            Use <strong>Clear All Templates</strong> to remove all template content.
            <strong> Warning:</strong> Checking or regenerating a template will replace any existing
            content in that field.
          </Typography>
        </Box>
      </Box>
    )}

    <Box sx={{ overflowX: 'auto', mb: showComments ? 2 : 0 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #ddd', tableLayout: 'auto' }}>
        <colgroup>
          <col style={{ width: hideTemplateColumn ? '35%' : '35%' }} />
          {!hideTemplateColumn && <col style={{ width: '15%' }} />}
          <col style={{ width: hideTemplateColumn ? '65%' : '50%' }} />
        </colgroup>
        <thead>
          <tr>
            <th
              style={{
                border: '1px solid #ddd',
                padding: '12px',
                backgroundColor: '#e9ecef',
                fontWeight: 'bold',
                textAlign: 'left',
              }}
            >
              Template Language Field
            </th>
            {!hideTemplateColumn && (
            <th
              style={{
                border: '1px solid #ddd',
                padding: '12px',
                backgroundColor: '#e9ecef',
                fontWeight: 'bold',
                textAlign: 'center',
              }}
            >
              Template
            </th>
            )}
            <th
              style={{
                border: '1px solid #ddd',
                padding: '12px',
                backgroundColor: '#e9ecef',
                fontWeight: 'bold',
                textAlign: 'left',
              }}
            >
              Description
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <td
                style={{
                  border: '1px solid #ddd',
                  padding: '12px',
                  backgroundColor: '#f5f5f5',
                  fontWeight: 'bold',
                  verticalAlign: 'top',
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  {row.label}
                </Typography>
              </td>
              {!hideTemplateColumn && (
              <td
                style={{
                  border: '1px solid #ddd',
                  padding: '12px',
                  backgroundColor: '#f5f5f5',
                  textAlign: 'center',
                  verticalAlign: 'top',
                }}
              >
                {row.notesOnly ? null : (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={templateSelections[row.key] || false}
                      onClick={(e) => {
                        if (
                          !templateSelections[row.key] ||
                          templateCheckboxDisabled ||
                          Boolean(templateLlmLoadingField) ||
                          e.shiftKey
                        ) {
                          return;
                        }
                        // Already checked: click again regenerates (do not uncheck)
                        e.preventDefault();
                        e.stopPropagation();
                        handleTemplateCheckboxChange(
                          row.key,
                          true,
                          setValues,
                          setFieldValue,
                          values,
                          valuesSection,
                          { regenerate: true }
                        );
                      }}
                      onChange={(e) => {
                        // Already checked: regenerate is handled by onClick only.
                        if (templateSelections[row.key]) {
                          if (!e.target.checked && e.nativeEvent?.shiftKey) {
                            handleTemplateCheckboxChange(
                              row.key,
                              false,
                              setValues,
                              setFieldValue,
                              values,
                              valuesSection
                            );
                          }
                          return;
                        }
                        handleTemplateCheckboxChange(
                          row.key,
                          e.target.checked,
                          setValues,
                          setFieldValue,
                          values,
                          valuesSection
                        );
                      }}
                      disabled={
                        templateCheckboxDisabled || Boolean(templateLlmLoadingField)
                      }
                      size="small"
                    />
                  }
                  label=""
                  sx={{ margin: 0 }}
                />
                )}
              </td>
              )}
              <td
                style={{
                  border: '1px solid #ddd',
                  padding: '0px',
                  verticalAlign: 'top',
                  height: 'auto',
                  minHeight: '80px',
                  position: 'relative',
                }}
              >
                <Field
                  name={`${valuesSection}.${row.key}`}
                  render={({ field }) => {
                    const normalizedField = {
                      ...field,
                      value: Array.isArray(field.value)
                        ? field.value.filter(Boolean).join('\n\n')
                        : (field.value ?? ''),
                    };
                    const fieldDisabled = isFormDisabled || row.readOnlyAssembled;
                    return cptColoredPreview && !row.notesOnly ? (
                      <CptTemplateTextField
                        field={normalizedField}
                        disabled={fieldDisabled}
                        placeholder={row.placeholder}
                        showCptPreview
                      />
                    ) : (
                      <TextField
                        {...normalizedField}
                        multiline
                        variant="outlined"
                        fullWidth
                        disabled={fieldDisabled}
                        placeholder={row.placeholder}
                        sx={{
                          width: '100%',
                          '& fieldset': { border: 'none' },
                        }}
                      />
                    );
                  }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Box>

    {showComments && (
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            name={commentsFieldName}
            label="Additional Notes"
            value={commentsValue}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isFormDisabled}
            placeholder="Enter any additional notes for this section"
          />
        </Grid>
      </Grid>
    )}
  </Box>
  );
};

export default TemplateLanguageFieldsTable;
